// Phone-number equality across formats (+19175550123 vs (917) 555-0123).
// Mirrors the backend's strip_country_code last-10-digit convention.
// Anything with a letter in it (a SIP identity, "anonymous") is not a phone
// number, whatever digits it happens to contain.
export const lastTenDigits = (value: string) =>
  /[a-z]/i.test(value) ? "" : value.replace(/\D/g, "").slice(-10);

export const phonesMatch = (a?: string | null, b?: string | null) => {
  const left = lastTenDigits(a ?? "");
  const right = lastTenDigits(b ?? "");
  return left.length === 10 && left === right;
};

// N11 and the other short service codes are dialed like an extension but are
// never a colleague.
const SERVICE_CODES = new Set([
  "211",
  "311",
  "411",
  "511",
  "611",
  "711",
  "811",
  "911",
  "933",
  "988"
]);

// An internal leg's peer is a bare extension ("101", "2998"), never a full
// phone number — so its shape alone tells a colleague from a patient.
export const isInternalExtension = (value?: string | null) => {
  const digits = (value ?? "").trim();
  return /^\d{2,6}$/.test(digits) && !SERVICE_CODES.has(digits);
};

// A leg placed from a colleague's web softphone does not carry their extension
// but their WebRTC device's SIP identity, "user_01m20tyr0hff8a0m334bbhmpht-wrtc"
// (ENG-2061). The part before "-wrtc" is their DialStack user id, which the
// voice directory also lists, so the colleague can still be named.
// Case-insensitive so a differently-cased id still counts as a colleague
// rather than falling through to the patient path it was mistaken for.
const WEBRTC_DEVICE_ID = /^(user_[0-9a-z]+)-wrtc$/i;

export const webrtcDeviceUserId = (value?: string | null) =>
  WEBRTC_DEVICE_ID.exec((value ?? "").trim())?.[1] ?? null;

// A colleague's line, however the phone system names it.
export const isColleagueLine = (value?: string | null) =>
  isInternalExtension(value) || webrtcDeviceUserId(value) !== null;
